import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, skipWhile, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NavigationUtils } from '../../shared/utils/navigation-utils';
import { selectOAuthProperties } from '../state/core.selectors';
import { v4 as uuidv4 } from 'uuid';

/**
 * Auth interceptor. Intercepts HTTP requests. If there is a valid auth token an `Authentication` header is added to the request.
 * Otherwise, the request is forwarded to the webeam endpoint to request a valid auth token.
 */

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    readonly HEADER_TRANSACTION_ID: string = 'transaction-id';
    readonly HEADER_CLIENT: string = 'client';
    readonly CLIENT: string = 'sera-frontend';

    constructor(private readonly store: Store) {}

    private cloneRequestWithAuthToken(req: HttpRequest<any>, authToken: string): HttpRequest<any> {
        const reqHeaderWithAuthToken = req.headers
            .set('Authorization', 'Bearer ' + authToken)
            .set(this.HEADER_TRANSACTION_ID, uuidv4())
            .set(this.HEADER_CLIENT, this.CLIENT);
        return req.clone({ headers: reqHeaderWithAuthToken });
    }

    private getAuthTokenFromUrlFragmentIfPresent(urlFragment: string): string {
        const urlFragmentSections = urlFragment.split('&');
        for (const element of urlFragmentSections) {
            const urlFragmentKeyValuePair = element.split('=');
            if (decodeURIComponent(urlFragmentKeyValuePair[0]) === 'id_token') {
                return decodeURIComponent(urlFragmentKeyValuePair[1]);
            }
        }
        return '';
    }

    private isTokenValid(authToken: string): boolean {
        if (authToken) {
            // Parsing the idToken to extract its content, and mainly check the expiry timestamp, to see if it has passed.
            let idTokenParsed = this.parseToken(authToken);
            return !!(idTokenParsed.exp && new Date(idTokenParsed.exp * 1000 - 5000).getTime() > Date.now());
        }
        return false;
    }

    private parseToken(token: string): { exp: number } {
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    }

    private requestAuthorization(redirectUrl: string): void {
        //set the redirect url to route to after authenticating with webeam
        window.localStorage.setItem('redirectUrl', redirectUrl);
        this.store
            .select(selectOAuthProperties)
            .pipe(
                skipWhile(oAuthProperties => oAuthProperties == null),
                take(1)
            )
            .subscribe(oAuthProperties => {
                const url =
                    oAuthProperties?.url +
                    '/' +
                    oAuthProperties?.authorizePath +
                    '?response_type=' +
                    encodeURI('id_token') +
                    '&client_id=' +
                    encodeURI(oAuthProperties!.clientId) +
                    '&redirect_uri=' +
                    encodeURI(oAuthProperties!.redirectUrl) +
                    '&scope=' +
                    encodeURI('\n' + 'openid profile email bmwids groups') +
                    '&nonce=' +
                    encodeURI('');
                NavigationUtils.openUrl(url);
            });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Exclude requests that should not be intercepted, like loadOAuthProperties.
        if (req.headers.get('skip')) {
            return next.handle(req);
        }

        // Check for a print_token query parameter in the URL.
        // The token is in the hash fragment (e.g. /#/path?print_token=...), so parse from window.location.hash.
        const hashQueryString = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
        const urlParams = new URLSearchParams(hashQueryString);
        const printToken = urlParams.get('print_token');
        if (printToken) {
            req = this.cloneRequestWithAuthToken(req, printToken);
            return next.handle(req);
        }

        let authToken: string | null = window.localStorage.getItem('authorizationData');

        // Check if the token is valid: check if it is filled, plus check if its date is past expiry or not.
        if (authToken && this.isTokenValid(authToken)) {
            req = this.cloneRequestWithAuthToken(req, authToken);
            return next.handle(req);
        } else if (this.isTestAuthEnabled()) {
            req = this.getTestByPassRequest(req);
            return next.handle(req);
        }

        authToken = this.getAuthTokenFromUrlFragmentIfPresent(window.location.hash.substring(2));
        if (authToken) {
            window.localStorage.setItem('authorizationData', authToken);
            req = this.cloneRequestWithAuthToken(req, authToken);
            // check if we need to redirect the user to the original link
            const redirectUrl = window.localStorage.getItem('redirectUrl');
            if (redirectUrl) {
                window.localStorage.removeItem('redirectUrl');
                NavigationUtils.openUrl(redirectUrl);
            } else {
                NavigationUtils.openUrl('/');
            }

            return next.handle(req);
        }

        // If this fails, ask for it again.
        this.requestAuthorization(window.location.href);
        return next.handle(req);
    }

    isTestAuthEnabled() {
        return !!environment?.authTestEnabled;
    }

    getTestAuthUser() {
        return environment?.authTestUser;
    }

    getTestByPassRequest(req: HttpRequest<any>) {
        let testAuthUser = this.getTestAuthUser();
        if (testAuthUser && !req.headers.get('auth-test-user')) {
            let headers = req.headers.set('auth-test-user', testAuthUser).set(this.HEADER_TRANSACTION_ID, uuidv4()).set(this.HEADER_CLIENT, this.CLIENT);
            return req.clone({ headers });
        }
        return req;
    }
}
